import type { Command } from './types';
import { exportMarkdown } from '@/export/exportMarkdown';
import { exportTextOutline } from '@/export/exportTextOutline';
import { exportSvg } from '@/export/exportSvg';
import { exportPng } from '@/export/exportPng';
import { exportJson } from '@/export/exportJson';
import { downloadFile, downloadBlob } from '@/export/downloadFile';
import { triggerExport, pollExportUntilComplete } from '@/api/exports';
import { IS_BACKEND_CONFIGURED } from '@/api/config';

/** Sanitize a title string for use as a filename. */
function toFilename(title: string): string {
  return title.replace(/[^a-zA-Z0-9_\- ]/g, '').trim() || 'mindmap';
}

/** Find the SVG canvas element in the DOM. */
function getCanvasSvg(): SVGSVGElement | null {
  return document.querySelector('svg') as SVGSVGElement | null;
}

export const exportCommands: Command[] = [
  {
    id: 'export.markdown',
    label: 'Export as Markdown',
    category: 'export',
    execute(ctx) {
      const nodes = ctx.doc.getAllNodes();
      const meta = ctx.doc.getMeta();
      const md = exportMarkdown(nodes, meta.rootNodeId);
      downloadFile(`${toFilename(meta.title)}.md`, md, 'text/markdown');
    },
  },
  {
    id: 'export.textOutline',
    label: 'Export as Text Outline',
    category: 'export',
    execute(ctx) {
      const nodes = ctx.doc.getAllNodes();
      const meta = ctx.doc.getMeta();
      const text = exportTextOutline(nodes, meta.rootNodeId);
      downloadFile(`${toFilename(meta.title)}.txt`, text, 'text/plain');
    },
  },
  {
    id: 'export.svg',
    label: 'Export as SVG',
    category: 'export',
    execute(ctx) {
      const svgEl = getCanvasSvg();
      if (!svgEl) return;

      const meta = ctx.doc.getMeta();
      const svgString = exportSvg(svgEl);
      downloadFile(`${toFilename(meta.title)}.svg`, svgString, 'image/svg+xml');
    },
  },
  {
    id: 'export.png',
    label: 'Export as PNG',
    category: 'export',
    execute(ctx) {
      const svgEl = getCanvasSvg();
      if (!svgEl) return;

      const meta = ctx.doc.getMeta();
      const svgString = exportSvg(svgEl);

      // Parse width/height from the generated SVG
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
      const svgRoot = svgDoc.documentElement;
      const width = parseFloat(svgRoot.getAttribute('width') ?? '1920');
      const height = parseFloat(svgRoot.getAttribute('height') ?? '1080');

      void exportPng(svgString, width, height).then((blob) => {
        downloadBlob(`${toFilename(meta.title)}.png`, blob);
      });
    },
  },
  {
    id: 'export.json',
    label: 'Export as JSON',
    category: 'export',
    execute(ctx) {
      const meta = ctx.doc.getMeta();
      const json = exportJson(ctx.doc);
      downloadFile(`${toFilename(meta.title)}.json`, json, 'application/json');
    },
  },
  {
    id: 'export.pdf',
    label: 'Export as PDF',
    category: 'export',
    canExecute() {
      return IS_BACKEND_CONFIGURED;
    },
    execute(ctx) {
      const mapId = localStorage.getItem('ideatwig_last_map');
      if (!mapId) return;

      const meta = ctx.doc.getMeta();
      const filename = `${toFilename(meta.title)}.pdf`;

      void (async () => {
        const { jobId } = await triggerExport(mapId, 'pdf');
        const downloadUrl = await pollExportUntilComplete(jobId);
        // Trigger download via a temporary link
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      })();
    },
  },
];
